import type { RegistrationIntakeModelView } from '@actschurch/ccms-sdk';
import { cleanText, PERSON_FIELDS } from './rules';
import {
  buildPerson, emptyPerson, sameText, type Candidates, type PersonResult, type RecordResult,
} from './people';

type Participant = NonNullable<RegistrationIntakeModelView['participants']>[number];

/** A participant's own values. Note the intake calls the first name `name`. */
const ownOf = (p: Participant): Candidates => ({
  firstName: [p.name], surname: [p.surname], email: [p.email],
  phone: [p.phone], gender: [p.gender], dateOfBirth: [p.dateOfBirth],
});

/** The applicant's top-level values. There is no date of birth up here. */
const applicantOf = (d: RegistrationIntakeModelView): Candidates => ({
  firstName: [d.firstName], surname: [d.surname], email: [d.email],
  phone: [d.phone, d.altContact], gender: [d.gender], dateOfBirth: [],
});

/**
 * Who is attending the course, and where each of their values lives.
 *
 *  A. `participants[]` not empty → A = the entry marked main (else the first), B = the other entry.
 *  B. empty + for self           → A = the applicant (no date of birth exists up there, so that field
 *                                   can only come from Source 3).
 *  C. empty + for someone else   → nobody to enrol. The applicant is the contact only and is never
 *                                   used as Person A.
 *
 * Person B only ever reads their own entry. Person A may fall back to the applicant, but only on a
 * self sign-up, and with two people only when the first names match, so one person's details can never
 * leak into the other.
 *
 * ⚠️ There is no custom `date_of_birth` answer on intakes (unlike care requests), so a participant
 * without a date of birth stays missing until Source 3.
 */
export function resolveIntake(
  d: RegistrationIntakeModelView,
  ctx: { id: number; reference: string; listLocation: string | null },
): RecordResult {
  const parts = d.participants ?? [];
  const forSelf = d.isForSelf !== false; // only an explicit false means "for someone else"
  const location = cleanText(ctx.listLocation) ?? cleanText(d.locationName);
  const applicant = applicantOf(d);

  let a: PersonResult;
  let b: PersonResult | null = null;
  let extraPeople = 0;

  if (parts.length > 0) {
    const main = parts.find((p) => p.isMain) ?? parts[0];
    const other = parts.find((p) => p !== main) ?? null;
    extraPeople = Math.max(0, parts.length - 2);

    const fallbackApplies = forSelf && (parts.length === 1 || sameText(main.name, d.firstName));
    const cand = ownOf(main);
    if (fallbackApplies) {
      for (const f of PERSON_FIELDS) if (f !== 'dateOfBirth') cand[f] = [...cand[f], ...applicant[f]];
    }
    a = buildPerson(cand, main.memberID ?? (fallbackApplies ? d.matchedMemberID ?? null : null));
    if (other) b = buildPerson(ownOf(other), other.memberID ?? null);
  } else if (forSelf) {
    a = buildPerson(applicant, d.matchedMemberID ?? null);
  } else {
    a = emptyPerson();
  }

  return {
    id: ctx.id, reference: ctx.reference, location, a, b, extraPeople, failed: false,
    // The sample intake has no location at all, so an empty one is normal, not a gap.
    requireLocation: false,
  };
}
